import React, { useMemo, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import { tokens } from "../../theme";
import { IconCalendar, IconChevronDown, IconClose } from "./icons";
import { useIsMobile } from "../../hooks/useIsMobile";

registerLocale("es", es);

/** Atajos: cubren el 90% de lo que se busca sin abrir el calendario. */
const PRESETS = [
  { id: "hoy", label: "Hoy", range: () => [moment().startOf("day"), moment().endOf("day")] },
  {
    id: "ayer",
    label: "Ayer",
    range: () => [
      moment().subtract(1, "day").startOf("day"),
      moment().subtract(1, "day").endOf("day"),
    ],
  },
  {
    id: "7",
    label: "Últimos 7 días",
    range: () => [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
  },
  {
    id: "30",
    label: "Últimos 30 días",
    range: () => [moment().subtract(29, "days").startOf("day"), moment().endOf("day")],
  },
  {
    id: "mes",
    label: "Este mes",
    range: () => [moment().startOf("month"), moment().endOf("month")],
  },
  {
    id: "mesPasado",
    label: "Mes pasado",
    range: () => [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  },
  {
    id: "anio",
    label: "Este año",
    range: () => [moment().startOf("year"), moment().endOf("year")],
  },
];

const formatLabel = (from, to) => {
  if (!from && !to) return "Fecha";
  const f = moment(from);
  const t = to ? moment(to) : null;
  if (!t) return `Desde ${f.format("DD/MM/YY")}`;
  if (f.isSame(t, "day")) return f.format("DD/MM/YY");
  if (f.isSame(t, "year")) return `${f.format("DD/MM")} – ${t.format("DD/MM/YY")}`;
  return `${f.format("DD/MM/YY")} – ${t.format("DD/MM/YY")}`;
};

/**
 * Filtro de rango de fechas: atajos a la izquierda y calendario de rango.
 * Devuelve momentos ya recortados al día (inicio y fin) por `onChange`.
 */
const DateRangeFilter = ({ from, to, onChange, label = "Fecha", fullWidth = false }) => {
  const [anchor, setAnchor] = useState(null);
  const isMobile = useIsMobile(768);
  const active = Boolean(from || to);

  const [start, end] = useMemo(
    () => [from ? new Date(from) : null, to ? new Date(to) : null],
    [from, to]
  );

  const applyPreset = (preset) => {
    const [f, t] = preset.range();
    onChange(f.toDate(), t.toDate());
    setAnchor(null);
  };

  const handleCalendarChange = (dates) => {
    const [nextStart, nextEnd] = dates;
    if (nextStart && nextEnd) {
      onChange(moment(nextStart).startOf("day").toDate(), moment(nextEnd).endOf("day").toDate());
      setAnchor(null);
      return;
    }
    onChange(nextStart ? moment(nextStart).startOf("day").toDate() : null, null);
  };

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        role="button"
        aria-label="Filtrar por fecha"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "7px",
          // Dentro de un formulario se comporta como un campo más; en una barra
          // de filtros, como un chip que se apaga y se prende.
          minHeight: fullWidth ? { xs: 44, md: 40 } : { xs: 44, md: 36 },
          width: fullWidth ? "100%" : "auto",
          padding: "0 12px",
          borderRadius: `${tokens.radiusControl}px`,
          fontSize: fullWidth ? { xs: "15px", md: "14px" } : "13px",
          whiteSpace: "nowrap",
          cursor: "pointer",
          flexShrink: 0,
          border: `1px solid ${active && !fullWidth ? tokens.graphite : tokens.border}`,
          backgroundColor: active && !fullWidth ? tokens.graphite : tokens.surface,
          color: active && !fullWidth ? "#fff" : active ? tokens.ink : tokens.ink3,
          fontWeight: active ? 500 : 400,
          "&:hover": { borderColor: tokens.borderStrong },
        }}
      >
        <IconCalendar size={15} />
        <Box component="span" sx={{ flexGrow: fullWidth ? 1 : 0, textAlign: "left" }}>
          {active ? formatLabel(from, to) : label}
        </Box>
        {active ? (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null, null);
            }}
            aria-label="Quitar filtro de fecha"
            sx={{ display: "flex", marginLeft: "2px", opacity: 0.75 }}
          >
            <IconClose size={14} />
          </Box>
        ) : (
          <IconChevronDown size={14} />
        )}
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              marginTop: "6px",
              border: `1px solid ${tokens.border}`,
              borderRadius: `${tokens.radiusCard}px`,
              boxShadow: tokens.shadowMd,
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: "2px",
              padding: "10px",
              minWidth: { sm: 168 },
              borderRight: { sm: `1px solid ${tokens.borderSoft}` },
              borderBottom: { xs: `1px solid ${tokens.borderSoft}`, sm: "none" },
            }}
          >
            {PRESETS.map((preset) => (
              <Box
                key={preset.id}
                onClick={() => applyPreset(preset)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 36,
                  padding: "0 10px",
                  borderRadius: "7px",
                  fontSize: "13px",
                  color: tokens.ink2,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: tokens.borderSoft, color: tokens.ink },
                }}
              >
                {preset.label}
              </Box>
            ))}

            <Box
              onClick={() => {
                onChange(null, null);
                setAnchor(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                minHeight: 36,
                padding: "0 10px",
                borderRadius: "7px",
                fontSize: "13px",
                fontWeight: 500,
                color: tokens.brandDark,
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: tokens.brandTint2 },
              }}
            >
              Cualquier fecha
            </Box>
          </Box>

          <Box className="sup-datepicker" sx={{ padding: "10px" }}>
            <DatePicker
              locale="es"
              selectsRange
              inline
              startDate={start}
              endDate={end}
              onChange={handleCalendarChange}
              monthsShown={isMobile ? 1 : 2}
              maxDate={new Date()}
              showPopperArrow={false}
              calendarStartDay={1}
            />
            <Typography sx={{ fontSize: "11.5px", color: tokens.ink3, paddingTop: "6px" }}>
              Elegí el día de inicio y después el de fin.
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeFilter;
